import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Clock,
  Calendar,
  DollarSign,
  HelpCircle,
  AlertCircle,
  Download,
  ExternalLink,
  User,
  Info,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import MainLayout from "@/components/layouts/MainLayout";

const ACNRegistration = () => {
  return (
    <>
      <Helmet>
        <title>ACN Registration | Numberz Insight</title>
        <meta
          name="description"
          content="Register for an Australian Company Number (ACN) with Numberz Insight. Professional assistance with ACN applications and company registration. Updated for 2025 requirements."
        />
        <meta
          name="keywords"
          content="ACN registration, Australian Company Number, company registration, tax agent, Numberz Insight, ASIC, director ID, 2025"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30 dark:from-background dark:to-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Badge className="mb-4" variant="outline">
                    Company Registration
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                    Australian Company Number Registration
                  </h1>
                  <p className="text-xl text-muted-foreground mb-8">
                    Get your Australian Company Number quickly and hassle-free
                    with our expert assistance. Updated for 2025 ASIC
                    requirements.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="gap-2">
                      Start Application <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2"
                      asChild
                    >
                      <Link to="/contact">Get Expert Advice</Link>
                    </Button>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="ACN Registration"
                    className="w-full h-auto object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-16 bg-background">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Benefits of ACN Registration
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  An Australian Company Number (ACN) is essential for
                  establishing a company in Australia and offers numerous
                  benefits.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Legal Entity Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Establish a separate legal entity with limited liability
                      protection for shareholders.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Business Credibility
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Enhance your business credibility and professional image
                      with customers and partners.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                      Perpetual Succession
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Your company continues to exist regardless of changes in
                      shareholders or directors.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Director ID Alert */}
          <section className="py-8 bg-background">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <Alert className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                <AlertTitle className="text-amber-800 dark:text-amber-400 font-medium">
                  New Director ID Requirement
                </AlertTitle>
                <AlertDescription className="text-amber-700 dark:text-amber-300">
                  As of 2025, all company directors are required by law to apply
                  for a director identification number (director ID). This is a
                  mandatory requirement before registering a company and
                  obtaining an ACN.
                  <a
                    href="https://asic.gov.au/for-business/running-a-company/company-officeholder-duties/director-identification-number/"
                    className="underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more about director ID requirements
                  </a>
                  .
                </AlertDescription>
              </Alert>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  The ACN Registration Process
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Our streamlined process makes registering your ACN simple and
                  efficient.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">1</span>
                    </div>
                    <CardTitle>Consultation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We'll discuss your business needs and help you determine
                      the right company structure. We'll also guide you through
                      the director ID application process.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">2</span>
                    </div>
                    <CardTitle>Documentation</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We'll prepare all necessary documents including company
                      constitution and ASIC forms.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <span className="text-xl font-bold text-primary">3</span>
                    </div>
                    <CardTitle>Registration</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      We'll submit your application to ASIC and obtain your ACN,
                      typically within 1-2 business days.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 bg-background">
            <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-muted-foreground">
                  Common questions about ACN registration in Australia.
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>What is an ACN?</AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      An Australian Company Number (ACN) is a unique 9-digit
                      number issued by the Australian Securities and Investments
                      Commission (ASIC) when a company is registered. It serves
                      as the company's identifier for all dealings with ASIC.
                    </p>
                    <p className="text-muted-foreground">
                      All companies registered under the Corporations Act 2001
                      must have an ACN, which must be displayed on all company
                      documents, including letterheads, invoices, and notices.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6">
                  <AccordionTrigger>
                    What is a director ID and why do I need one?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      A director ID (director identification number) is a unique
                      identifier that a company director needs to apply for once
                      and keep forever. As of 2025, all directors must have a
                      director ID before they can be appointed as a director.
                    </p>
                    <p className="text-muted-foreground mb-2">
                      The director ID requirement helps to:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        Prevent the use of false or fraudulent director
                        identities
                      </li>
                      <li>
                        Make it easier to trace directors' relationships with
                        companies
                      </li>
                      <li>
                        Identify and eliminate director involvement in unlawful
                        activity
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      Our team can guide you through the director ID application
                      process as part of our ACN registration service.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    What's the difference between an ACN and ABN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      An ACN (Australian Company Number) is issued by ASIC when
                      you register a company, while an ABN (Australian Business
                      Number) is issued by the Australian Business Register for
                      tax purposes.
                    </p>
                    <p className="text-muted-foreground">
                      Companies need both an ACN and ABN. The ABN for a company
                      includes the ACN within it (the ABN is an 11-digit number
                      that includes the 9-digit ACN plus two additional digits).
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    How long does it take to get an ACN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground">
                      With our professional service, ACN registration typically
                      takes 1-2 business days once all required information and
                      documentation are provided. This includes company name
                      reservation, preparation of necessary documents, and
                      submission to ASIC.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    What information do I need to provide?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      To register for an ACN, you'll need to provide:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Proposed company name</li>
                      <li>Company type (e.g., proprietary limited)</li>
                      <li>Registered office address</li>
                      <li>Principal place of business</li>
                      <li>
                        Details of directors and shareholders (including names,
                        addresses, and dates of birth)
                      </li>
                      <li>Share structure information</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>
                    What are the ongoing obligations after getting an ACN?
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-2">
                      After obtaining an ACN, companies have ongoing obligations
                      including:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        Annual review and payment of ASIC annual review fee
                      </li>
                      <li>
                        Notifying ASIC of changes to company details within
                        specified timeframes
                      </li>
                      <li>Maintaining proper company records</li>
                      <li>
                        Financial reporting requirements (depending on company
                        size)
                      </li>
                      <li>Tax obligations including company tax returns</li>
                    </ul>
                    <p className="text-muted-foreground mt-2">
                      Our ongoing compliance services can help you meet these
                      obligations.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary/5 dark:bg-primary/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Register Your Company?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    Our team of professionals is ready to help you with your ACN
                    registration. Contact us today to get started.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Expert guidance throughout the process</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Director ID application assistance</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Fast and efficient service</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Ongoing compliance support</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Start Your ACN Application</CardTitle>
                      <CardDescription>
                        Fill out the form below and one of our professionals
                        will contact you shortly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg">
                          Start Application
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="py-16 bg-background">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Useful Resources</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Additional information about company registration in
                  Australia.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ASIC
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Official information from the Australian Securities and
                      Investments Commission.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://asic.gov.au/for-business/registering-a-company/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ASIC <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <User className="h-5 w-5 mr-2 text-primary" />
                      Director ID Application
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Apply for your director identification number through the
                      Australian Business Registry Services.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://www.abrs.gov.au/director-identification-number"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Apply for Director ID{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Business.gov.au
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Government resources for businesses, including company
                      registration information.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://business.gov.au/registrations/register-a-company"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Business.gov.au{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Company Name Check
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Check if your desired company name is available for
                      registration.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://connectonline.asic.gov.au/RegistrySearch/faces/landing/NameAvail.jspx"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Check Name Availability{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default ACNRegistration;
